import { codeableConceptLabel } from '@ctw/shared/api/fhir/codeable-concept';
import { SYSTEM_ENRICHMENT, SYSTEM_NULL_FLAVOR } from '@ctw/shared/api/fhir/system-urls';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { Coding, DiagnosticReport } from 'fhir/r4';
import { compact } from 'lodash-es';
import pluralize from 'pluralize';
import { findReference } from '../resource-helper';
import { FHIRModel } from './fhir-model';
import { PatientModel } from './patient';
import { PractitionerModel } from './practitioner';

export class DiagnosticReportModel extends FHIRModel<DiagnosticReport> {
  public kind = 'DiagnosticReport' as const;

  public get displayName() {
    return this.loincCode?.display || codeableConceptLabel(this.resource.code);
  }

  public get date() {
    return (
      this.resource.effectivePeriod?.start ||
      this.resource.effectiveDateTime ||
      this.resource.issued
    );
  }

  public get numResultsDisplay() {
    const results = this.resource.result?.length ?? 0;
    if (results === 0) {
      return undefined;
    }

    return pluralize('result', results, true);
  }

  public get dateDisplay() {
    return this.date ? formatFHIRDate(this.date) : undefined;
  }

  public get performer() {
    const performers = this.resource.performer ?? [];
    const practitioner = findReference('Practitioner', undefined, undefined, performers[0]);
    if (practitioner) {
      return new PractitionerModel(practitioner).fullName;
    }
    return performers[0]?.display;
  }

  public override get patientOrganizationName(): string | undefined {
    const reference = findReference(
      'Patient',
      this.resource.contained,
      this.includedResources,
      this.resource.subject,
    );

    if (reference) {
      return new PatientModel(reference, this.includedResources).organizationName;
    }

    return undefined;
  }

  public get title() {
    return this.displayName;
  }

  public get observationIds() {
    return compact(this.resource.result?.map((result) => result.reference?.split('/')[1]));
  }

  public get loincCode() {
    return findEnrichedLoincCoding(this.resource.code.coding);
  }
}

export const findEnrichedLoincCoding = (coding?: Array<Coding>) => {
  if (!coding) {
    return undefined;
  }
  return coding.find(
    (x) =>
      x.extension?.find(
        (ext) => ext.valueString === 'LOINC Standardization' && ext.url === SYSTEM_ENRICHMENT,
      ) &&
      x.display &&
      x.system !== SYSTEM_NULL_FLAVOR,
  );
};
